import React from 'react';
import { formatMoney, formatNumber } from '../../lib/formatMoney';

function formatDateLabel(value) {
  if (!value) return 'All time';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }).format(new Date(`${value}T00:00:00`));
  } catch (err) {
    return value;
  }
}

function formatDateRange(filters = {}) {
  if (!filters.start && !filters.end) return 'All time';
  return `${formatDateLabel(filters.start)} to ${formatDateLabel(filters.end)}`;
}

function buildFilterRows(filters = {}, labels = {}, currencyCode) {
  return [
    { label: 'Time period', value: labels.timePeriod || 'All time' },
    { label: 'Date range', value: formatDateRange(filters) },
    { label: 'Venue', value: labels.venue || 'All venues' },
    { label: 'Event', value: labels.event || 'All events' },
    { label: 'Product type', value: labels.productType || 'All types' },
    { label: 'Currency', value: currencyCode || 'DKK' },
  ];
}

function PdfCard({ label, value, detail }) {
  return (
    <div className="rounded border border-slate-200 bg-white p-3 shadow-sm">
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="mt-2 flex items-baseline justify-between gap-3">
        <div className="text-[18px] font-semibold text-slate-900">{value}</div>
        {detail ? <div className="text-[11px] text-slate-500">{detail}</div> : null}
      </div>
    </div>
  );
}

function SectionShell({ title, subtitle, children }) {
  return (
    <section className="break-after-page border-b border-slate-200 px-1 pb-8 last:border-b-0 last:pb-0" data-pdf-page="true">
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-[22px] font-semibold tracking-[-0.02em] text-slate-900">{title}</h2>
          {subtitle ? <p className="mt-1 text-[12px] text-slate-500">{subtitle}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function ReportScope({ filters, filterLabels, currencyCode }) {
  const rows = buildFilterRows(filters, filterLabels, currencyCode);

  return (
    <div className="mt-5 rounded border border-slate-200 bg-slate-50 p-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#201C35]">Report scope</div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        {rows.map((row) => (
          <div key={row.label} className="rounded border border-slate-200 bg-white px-3 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{row.label}</div>
            <div className="mt-1 text-[12px] font-semibold text-slate-800">{row.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RevenueBars({ revenueByDate = [], currencyCode }) {
  const items = revenueByDate.slice(-14);
  const maxRevenue = Math.max(...items.map((item) => Number(item.totalRevenue || 0)), 1);

  if (!items.length) {
    return <div className="rounded border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">No revenue trend data for this period.</div>;
  }

  return (
    <div className="rounded border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-sm font-semibold text-slate-800">Daily revenue</div>
        <div className="text-xs text-slate-500">Showing the latest {items.length} days</div>
      </div>
      <div className="flex h-[220px] items-end gap-2">
        {items.map((item) => {
          const amount = Number(item.totalRevenue || 0);
          const height = Math.max(12, Math.round((amount / maxRevenue) * 180));
          return (
            <div key={item.date} className="flex min-w-0 flex-1 flex-col items-center gap-2">
              <div className="flex h-[190px] w-full items-end">
                <div className="w-full rounded-t-sm bg-[#201C35]" style={{ height }} title={`${item.date}: ${formatMoney(amount, { currency: currencyCode })}`} />
              </div>
              <div className="w-full text-center text-[10px] text-slate-500">{formatDateLabel(item.date)}</div>
              <div className="w-full text-center text-[10px] font-semibold text-slate-700">{formatMoney(amount, { currency: currencyCode })}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DataTable({ columns, rows, rowKey }) {
  return (
    <div className="overflow-hidden rounded border border-slate-200 bg-white">
      <table className="w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={`px-3 py-2 text-left font-semibold ${column.align === 'right' ? 'text-right' : ''}`}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-6 text-center text-slate-500">
                No rows to display.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={rowKey(row)} className="border-t border-slate-100">
                {columns.map((column) => (
                  <td key={column.key} className={`px-3 py-2 text-slate-700 ${column.align === 'right' ? 'text-right' : ''}`}>
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const REPORT_VARIANTS = {
  overview: { summary: true, revenueTrend: true, aovTrend: false, products: true, events: true, venues: true },
  trends: { summary: false, revenueTrend: true, aovTrend: true, products: false, events: false, venues: false },
  products: { summary: false, revenueTrend: false, aovTrend: false, products: true, events: false, venues: false },
  events: { summary: false, revenueTrend: false, aovTrend: false, products: false, events: true, venues: false },
  venues: { summary: false, revenueTrend: false, aovTrend: false, products: false, events: false, venues: true },
};

export function PdfReportDocument({
  title = 'Venue Intelligence Revenue Report',
  reportVariant = 'overview',
  generatedAt,
  summary,
  currencyCode,
  filters,
  filterLabels = {},
  revenueByDate = [],
  aovByDate = [],
  revenueByProduct = [],
  topEventRows = [],
  topVenues = [],
}) {
  const sections = REPORT_VARIANTS[reportVariant] || REPORT_VARIANTS.overview;
  const totalRevenue = summary?.totalRevenue ?? 0;
  const invoiceCount = summary?.invoiceCount ?? 0;
  const averageOrderValue = summary?.averageOrderValue ?? 0;
  const unitsSold = summary?.unitsSold ?? 0;
  const generatedLabel = generatedAt ? new Date(generatedAt).toLocaleString() : new Date().toLocaleString();
  const dateRangeLabel = formatDateRange(filters);

  const productRows = [...(revenueByProduct || [])].slice(0, 5);
  const eventRows = [...(topEventRows || [])].slice(0, 5);
  const venueRows = [...(topVenues || [])].slice(0, 5);
  const aovRows = [...(aovByDate || [])].slice(-14);

  return (
    <div className="w-[1280px] bg-white px-8 py-8 text-slate-900">
      <section className="break-after-page mb-8" data-pdf-page="true">
        <div className="border-b border-slate-200 pb-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#201C35]">Venue Intelligence</div>
          <h1 className="mt-2 text-[30px] font-black tracking-[-0.04em] text-slate-900">{title}</h1>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-slate-500">
            <span>Generated {generatedLabel}</span>
            <span>Date range: {dateRangeLabel}</span>
            <span>Currency: {currencyCode || 'DKK'}</span>
          </div>
        </div>

        {sections.summary ? (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <PdfCard label="Total Revenue" value={formatMoney(totalRevenue, { currency: currencyCode })} detail={currencyCode} />
            <PdfCard label="Invoice Count" value={formatNumber(invoiceCount)} detail="Processed invoices" />
            <PdfCard label="Average Order Value" value={formatMoney(averageOrderValue, { currency: currencyCode })} detail="Per invoice" />
            <PdfCard label="Units Sold" value={formatNumber(unitsSold)} detail="Total quantity" />
          </div>
        ) : null}

        <ReportScope filters={filters} filterLabels={filterLabels} currencyCode={currencyCode} />
      </section>

      {sections.revenueTrend ? (
        <SectionShell title="Revenue Over Time" subtitle="Daily revenue trend from the selected dashboard period">
          <RevenueBars revenueByDate={revenueByDate} currencyCode={currencyCode} />
        </SectionShell>
      ) : null}

      {sections.aovTrend ? (
        <SectionShell title="Average Order Value Over Time" subtitle="Daily average order value for the selected period">
          <DataTable
            rowKey={(row) => row.date}
            columns={[
              { key: 'date', label: 'Date', render: (row) => formatDateLabel(row.date) },
              {
                key: 'averageOrderValue',
                label: 'AOV',
                align: 'right',
                render: (row) => formatMoney(row.averageOrderValue ?? row.aov ?? row.value ?? 0, { currency: currencyCode }),
              },
            ]}
            rows={aovRows}
          />
        </SectionShell>
      ) : null}

      {sections.products ? (
        <SectionShell title="Revenue by Product" subtitle="Top product contribution for the selected period">
          <DataTable
            rowKey={(row) => row.productName}
            columns={[
              { key: 'productName', label: 'Product' },
              { key: 'quantity', label: 'Qty', align: 'right', render: (row) => formatNumber(row.quantity || 0) },
              { key: 'totalRevenue', label: 'Revenue', align: 'right', render: (row) => formatMoney(row.totalRevenue || 0, { currency: currencyCode }) },
            ]}
            rows={productRows}
          />
        </SectionShell>
      ) : null}

      {sections.events ? (
        <SectionShell title="Top Events" subtitle="Events ranked by revenue contribution">
          <DataTable
            rowKey={(row) => `${row.eventId ?? row.eventName}`}
            columns={[
              { key: 'eventName', label: 'Event' },
              { key: 'invoiceCount', label: 'Invoices', align: 'right', render: (row) => formatNumber(row.invoiceCount || 0) },
              { key: 'quantity', label: 'Units', align: 'right', render: (row) => formatNumber(row.quantity || 0) },
              { key: 'totalRevenue', label: 'Revenue', align: 'right', render: (row) => formatMoney(row.totalRevenue || 0, { currency: currencyCode }) },
            ]}
            rows={eventRows}
          />
        </SectionShell>
      ) : null}

      {sections.venues ? (
        <SectionShell title="Top Venues" subtitle="Venues ranked by revenue contribution">
          <DataTable
            rowKey={(row) => `${row.venueId ?? row.venueName}`}
            columns={[
              { key: 'venueName', label: 'Venue' },
              { key: 'invoiceCount', label: 'Invoices', align: 'right', render: (row) => formatNumber(row.invoiceCount || 0) },
              { key: 'quantity', label: 'Units', align: 'right', render: (row) => formatNumber(row.quantity || 0) },
              { key: 'totalRevenue', label: 'Revenue', align: 'right', render: (row) => formatMoney(row.totalRevenue || 0, { currency: currencyCode }) },
            ]}
            rows={venueRows}
          />
        </SectionShell>
      ) : null}
    </div>
  );
}

export default PdfReportDocument;
